import Experience from "@folio-2026/experience"
import { AnimatedText, EVENTS, Grid, SmallButton } from "@folio-2026/shared"
import { useGSAP } from "@gsap/react"
import { gsap, ScrollTrigger } from "gsap/all"
import { useEffect, useRef } from "react"

import AddMessage from "./AddMessage"

gsap.registerPlugin(ScrollTrigger)

export default function Guestbook() {
  const triggerRef = useRef<HTMLDivElement | null>(null)
  const experience = useRef(Experience.getInstance())

  useEffect(() => {
    experience.current.on(EVENTS.Guestbook.Memory.Add, () => {
      console.log("show React form")
    })

    experience.current.on(EVENTS.Guestbook.Memory.AddClosed, () => {
      console.log("hide React form")
    })

    experience.current.on(EVENTS.Guestbook.Memory.AddSent, () => {
      console.log("hide React form and show success message")
    })
  }, [])

  useGSAP(
    ({ selector }) => {
      /**
       * Section enter animation
       */
      const enterTriggerEl = selector?.(
        "[data-guestbook-enter]",
      )[0] as HTMLDivElement
      ScrollTrigger.create({
        trigger: enterTriggerEl,
        start: "top bottom",
        end: "bottom bottom",
        markers: false,
        scrub: true,
        onEnter() {
          // Hide the grid and the memories
          experience.current.trigger(EVENTS.Guestbook.BeforeEnter)
        },
        onUpdate({ progress }) {
          // Go through the memories
          experience.current.trigger(EVENTS.Guestbook.Entering, [
            {
              progress,
            },
          ])
        },
        onLeave() {
          // Show the grid and the memories
          experience.current.trigger(EVENTS.Guestbook.Entered)
        },
      })

      /**
       * Section exit animation
       * Tracks when the user scrolls past the section
       */
      ScrollTrigger.create({
        trigger: triggerRef.current,
        start: "bottom bottom",
        end: "bottom top",
        markers: false,
        scrub: true,
        onUpdate({ progress }) {
          experience.current.trigger(EVENTS.Guestbook.Exiting, [
            {
              progress,
            },
          ])
        },
      })

      /**
       * Rest of the section
       * this ScrollTrigger wraps the projects thumbs and the last div
       * it is used to animate the camera position
       */
      const restOfSectionTriggerEl =
        enterTriggerEl.nextSibling as HTMLDivElement

      ScrollTrigger.create({
        trigger: restOfSectionTriggerEl,
        start: "top bottom",
        end: "bottom top",
        markers: false,
        scrub: true,
        onUpdate({ progress }) {
          // console.log("scroll trigger update", progress)
          experience.current.trigger(EVENTS.Guestbook.Scroll, [
            {
              progress,
            },
          ])
        },
      })
    },
    {
      scope: triggerRef,
    },
  )

  return (
    <>
      <AddMessage />

      <div ref={triggerRef}>
        <div data-guestbook-enter className="h-[300vh]" />
        <div className="h-[800vh]" />

        <div className="fixed top-0 left-0 h-screen w-full flex flex-col justify-center">
          <Grid>
            <div className="col-start-1 md:col-start-5 lg:col-start-7 col-span-3">
              <AnimatedText
                preventUpdate
                className="text-gray-700"
                data-font-bold
                content="]]]"
                tag="h1"
              />
              <AnimatedText
                preventUpdate
                className="text-gray-700"
                data-font-bold
                content="        []]"
                tag="h1"
              />
              <AnimatedText
                preventUpdate
                className="text-gray-700"
                data-font-bold
                content="      ]][[ROOM OF MEMORIES]]"
                tag="h1"
                highlight={{
                  substring: "ROOM OF MEMORIES",
                  className: "text-guestbook",
                }}
              />
              <AnimatedText
                preventUpdate
                className="text-gray-700"
                data-font-bold
                content="   ]]["
                tag="h1"
              />
              <span data-suka className="text-guestbook" />
            </div>
          </Grid>
          <Grid className="mt-8">
            <div className="col-start-2 ml-4 md:ml-0 md:col-start-4 lg:col-start-8 col-span-3 md:col-span-4 lg:col-span-3 max-w-md">
              <AnimatedText
                preventUpdate
                data-font-bold
                className="text-gray-600"
                content="Every ghost of a memory finds its sanctuary in the unwavering embrace of the timeless."
              />
            </div>
          </Grid>
          <Grid>
            <div className="col-start-1 md:col-start-5 lg:col-start-7 col-span-3">
              <AnimatedText
                preventUpdate
                className="text-gray-700 mt-4"
                data-font-bold
                content="     []]"
                tag="h1"
              />
            </div>
          </Grid>
          <Grid>
            <div className="col-start-2 ml-4 md:ml-0 md:col-start-4 lg:col-start-8 col-span-3 md:col-span-4 lg:col-span-3 max-w-md">
              <SmallButton
                id="guestbook-memory-add"
                preventUpdate
                className="mt-6 w-25"
                clickEvent={EVENTS.Guestbook.Memory.Add}
                hoverEvent={EVENTS.Guestbook.Memory.Hover}
                outEvent={EVENTS.Guestbook.Memory.Out}
              >
                ADD A MEMORY
              </SmallButton>
            </div>
          </Grid>
          <div className="absolute bottom-8 top-auto left-0 w-full text-center text-red-500">
            <AnimatedText
              preventUpdate
              className="text-gray-700"
              content="Scroll to continue"
            />
          </div>
        </div>
      </div>
    </>
  )
}
